import React, { useState, useEffect, useMemo } from 'react';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/topUpAddOns/addNumbersDialogStyle';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import AlternativeSelect, { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { SingleAccount } from 'utils/api/models/accounts/singleAccount';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import CustomValues, { ICustomNumberValue } from './subcomponents/CustomValues';
import Value from './subcomponents/Value';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import NoDepartments from './subcomponents/NoDepartments';
import NumbersTable from './subcomponents/NumbersTable';

import _ from 'lodash';

// @ts-ignore
const useStyles = makeStyles(styles);

interface AddNumberDialogProps {
	onClose: () => void;
	isOpen: boolean;
	numbers: SingleAccount[];
	handleAdd: (numbers: SingleAccount[]) => void;
	departmentsFetched: { [key: string]: SingleDepartment };
	accounts: { [key: string]: SingleCorpAccount };
}

interface IFilter<Numbers, Departments> {
	numbers: Numbers;
	departments: Departments extends undefined ? Numbers : Departments;
}

type IValueType = 'string' | 'array';

const filters: Array<keyof IFilter<string, undefined>> = ['numbers', 'departments'];

const value = (valueType: IValueType) => (valueType === 'string' ? '' : []);

const setEmpty = (valueType: IValueType) => {
	return {
		numbers: value(valueType),
		departments: value(valueType),
	};
};

const AddNumberDialog = ({
	isOpen,
	onClose,
	numbers,
	handleAdd,
	departmentsFetched,
	accounts,
}: AddNumberDialogProps) => {
	const [filterBy, setFilterBy] = useState<keyof IFilter<string, undefined>>(filters[0]);
	const [selected, setSelected] = useState<IFilter<string[], undefined>>(
		setEmpty('array') as IFilter<string[], undefined>
	);
	const [filtered, setFiltered] = useState<IFilter<SingleAccount[], SingleDepartment[]>>(
		setEmpty('array') as IFilter<SingleAccount[], SingleDepartment[]>
	);
	const [departments, setDepartments] = useState<{ [key: string]: SingleDepartment }>({});
	const [searchValue, setSearchValue] = useState<IFilter<string, undefined>>(
		setEmpty('string') as IFilter<string, undefined>
	);
	const [selectedAccount, setSelectedAccount] = useState<IAlternativeSelectItem | null>(null);
	const [customNumberValue, setCustomNumberValue] = useState<ICustomNumberValue>({
		value: '',
		error: false,
		success: false,
		loading: false,
	});
	const [customNumbers, setCustomNumbers] = useState<string[]>([]);
	const isSelected = !!selectedAccount?.id;
	const classes = useStyles();

	useEffect(() => {
		setSelected(setEmpty('array') as IFilter<string[], undefined>);
	}, [isOpen, filtered]);

	useEffect(() => {
		setSearchValue(setEmpty('string') as IFilter<string, undefined>);
	}, [filterBy]);

	useEffect(() => {
		setFiltered((filtered: IFilter<SingleAccount[], SingleDepartment[]>) => {
			const data = ({
				numbers: [...numbers],
				departments: [...Object.values(departments)],
			}[filterBy] as Array<SingleDepartment | SingleAccount>).filter(
				({ accountId }: { accountId: string }) => !isSelected || accountId === selectedAccount!.id
			);

			const filters = {
				numbers: ({ accountNum }: SingleAccount) => String(accountNum).includes(searchValue.numbers),
				departments: ({ name }: SingleDepartment) => String(name).includes(searchValue.departments),
			};

			if (searchValue[filterBy] === '') {
				return { ...filtered, [filterBy]: data };
			}

			return {
				...filtered,
				// @ts-ignore
				[filterBy]: data.filter(filters[filterBy]),
			};
		});
	}, [searchValue, numbers, departments, filterBy, selectedAccount, isSelected]);

	useEffect(() => {
		setDepartments(() => {
			return numbers.reduce((acc, value: SingleAccount) => {
				if (!value.deptId) {
					return acc;
				}

				return {
					...acc,
					[value.deptId]: {
						id: value.deptId,
						...departmentsFetched[value.deptId],
						numbers: !acc[value.deptId] ? [value] : [...acc[value.deptId].numbers, value],
					},
				};
			}, {});
		});
	}, [numbers, departmentsFetched]);

	const numbersSelected = useMemo(() => {
		const all = customNumbers.map((number) => ({ accountNum: number, custom: true }));
		let newSelected = null;
		if (filterBy === 'departments') {
			newSelected = [...all, ...selected[filterBy].map((index) => filtered.departments[index].numbers).flat()];
		} else {
			newSelected = [...all, ...selected[filterBy].map((index) => numbers[index])];
		}

		return _.uniqBy(newSelected, 'accountNum');
	}, [filterBy, selected, filtered, customNumbers, numbers]);

	return (
		<CustomDialog
			hideDialog={onClose}
			width={650}
			isOpen={isOpen}
			header={translate('payments.topup.addNumbersHeader')}
			id="add-numbers-dialog"
			dialogBody={
				<div className={classes.addNumbersDialog}>
					<CustomValues
						customNumberValue={customNumberValue}
						setCustomNumberValue={setCustomNumberValue}
						customNumbers={customNumbers}
						setCustomNumbers={setCustomNumbers}
					/>
					<AlternativeSelect
						label={translate('organization')}
						value={<Value selectedAccount={selectedAccount} />}
						onClick={(account) => {
							if (selectedAccount?.id === account.id || !account.id) {
								setSelectedAccount(null);
							} else {
								setSelectedAccount(account);
							}
						}}
						items={[
							{ name: translate('all.organizations'), id: null },
							...Object.values(accounts).map((account) => ({
								name: account.accountName,
								id: account.accountId,
							})),
						]}
						customIcons
						className={classes.accountPicker}
						selectedItems={isSelected ? [selectedAccount!.id] : []}
					/>
					<div className={classes.filterBy}>
						<CustomRadio
							label={translate('payments.topup.byNumbers')}
							checked={filterBy === 'numbers'}
							onChange={() => setFilterBy('numbers')}
						/>
						<CustomRadio
							label={translate('payments.topup.byDepartments')}
							checked={filterBy === 'departments'}
							onChange={() => setFilterBy('departments')}
						/>
					</div>
					<ConditionalRender
						show={filterBy === 'departments' && Object.values(departments).length === 0}
						onTrue={() => <NoDepartments departmentsFetched={departmentsFetched} />}
						onFalse={() => (
							<NumbersTable
								searchValue={searchValue}
								setSearchValue={setSearchValue}
								filterBy={filterBy}
								departmentsFetched={departmentsFetched}
								filtered={filtered}
								selected={selected}
								setSelected={setSelected}
							/>
						)}
					/>
				</div>
			}
			rightSideButtons={[
				{
					color: 'tertiary-light',
					text: translate('payments.topup.cancel'),
					onClick: onClose,
				},
				{
					color: 'primary-light',
					text: translate('payments.topup.add', numbersSelected.length),
					onClick: () => {
						handleAdd(numbersSelected);
						onClose();
					},
					disabled: numbersSelected.length === 0,
					id: 'dialog-add-numbers-button',
				},
			]}
		/>
	);
};

export default AddNumberDialog;
